import { RouteCodes } from '@strata/constants';

/** LaunchDarkly flag: special-handling icon on referral/treatment member name. */
// eslint-disable-next-line no-secrets/no-secrets -- LaunchDarkly flag key
export const SPECIAL_MEMBER_HANDLING_FLAG = 'showSpecialMemberHandling';

export const MemberAttributes = {
    AMMO: 'AMMO',
    LTC: 'LTC',
    VIP_OOS: 'VIP_OOS',
} as const;

export const SpecialHandlingScenario = {
    PREFERRED_PHARMACY: 'PREFERRED_PHARMACY',
    AMMO: 'AMMO',
    LTC: 'LTC',
    VIP_OOS: 'VIP_OOS',
} as const;

const ATTRIBUTE_ORDER = [
    MemberAttributes.AMMO,
    MemberAttributes.LTC,
    MemberAttributes.VIP_OOS,
] as const;

type Scenario = (typeof SpecialHandlingScenario)[keyof typeof SpecialHandlingScenario];

export interface SpecialMemberHandlingResult {
    /** First matching scenario (preferred pharmacy, else first attribute). */
    scenario: Scenario;
    /** All matching scenarios in display order. */
    scenarios: Scenario[];
    /** Tooltip lines (one string per match). */
    tooltip: string[];
}

const ATTRIBUTE_HANDLING: Record<
    (typeof ATTRIBUTE_ORDER)[number],
    { scenario: Scenario; tooltip: string }
> = {
    [MemberAttributes.AMMO]: {
        scenario: SpecialHandlingScenario.AMMO,
        tooltip: 'Patient out of scope: AMMO',
    },
    [MemberAttributes.LTC]: {
        scenario: SpecialHandlingScenario.LTC,
        tooltip: 'Patient out of scope: LTC',
    },
    [MemberAttributes.VIP_OOS]: {
        scenario: SpecialHandlingScenario.VIP_OOS,
        tooltip: 'Patient out of scope',
    },
};

/**
 * Collect all matching scenarios; tooltip is one string per match (rendered as lines).
 * Preferred pharmacy first (when present), then OOS attributes in AMMO → LTC → VIP_OOS order.
 */
export const resolveSpecialMemberHandling = ({
    attributes = [],
    isPreferredPharmacy = false,
    preferredPharmacyName,
}: {
    attributes?: string[];
    isPreferredPharmacy?: boolean;
    preferredPharmacyName?: string;
} = {}): SpecialMemberHandlingResult | null => {
    const matches: { scenario: Scenario; tooltip: string }[] = [];

    if (isPreferredPharmacy) {
        const trimmedPharmacyName = preferredPharmacyName?.trim() ?? '';
        const pharmacyLabel =
            trimmedPharmacyName.length > 0 ? trimmedPharmacyName : 'preferred pharmacy';
        matches.push({
            scenario: SpecialHandlingScenario.PREFERRED_PHARMACY,
            tooltip: `Must use patient-preferred pharmacy: ${pharmacyLabel}`,
        });
    }

    const attributeSet = new Set((attributes ?? []).map((value) => String(value).toUpperCase()));
    ATTRIBUTE_ORDER.forEach((attribute) => {
        if (attributeSet.has(attribute)) {
            matches.push(ATTRIBUTE_HANDLING[attribute]);
        }
    });

    if (matches.length === 0) {
        return null;
    }

    return {
        scenario: matches[0].scenario,
        scenarios: matches.map((match) => match.scenario),
        tooltip: matches.map((match) => match.tooltip),
    };
};

export const isPatientPreferredPharmacyRoute = (routeCode?: string | null): boolean =>
    routeCode === RouteCodes.MEMBER_PREFERENCE;
